"use client"

import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"

const TooltipProvider = TooltipPrimitive.Provider

const Tooltip = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>
>(({ children, ...props }, _ref) => {
  const [open, setOpen] = React.useState(false)
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const isTouchDevice = React.useRef(false)
  const mountedAt = React.useRef(Date.now())

  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleOpenChange = React.useCallback((newOpen: boolean) => {
    if (isTouchDevice.current) {
      return
    }
    if (newOpen && Date.now() - mountedAt.current < 300) {
      return
    }
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    if (newOpen) {
      setOpen(true)
    } else {
      closeTimeoutRef.current = setTimeout(() => {
        setOpen(false)
      }, 300)
    }
  }, [])

  const handleTouch = React.useCallback((e: React.TouchEvent) => {
    e.preventDefault()
    e.stopPropagation()
    isTouchDevice.current = true

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    setOpen((prev) => !prev)

    timeoutRef.current = setTimeout(() => {
      setOpen(false)
    }, 5000)
  }, [])

  React.useEffect(() => {
    mountedAt.current = Date.now()
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current)
      }
    }
  }, [])

  return (
    <TooltipPrimitive.Root
      open={open}
      onOpenChange={handleOpenChange}
      delayDuration={0}
      {...props}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type === TooltipTrigger) {
          return React.cloneElement(child as React.ReactElement<any>, {
            onTouchEnd: handleTouch,
            onClick: (e: React.MouseEvent) => {
              if (isTouchDevice.current) {
                e.preventDefault()
              }
            },
          })
        }
        return child
      })}
    </TooltipPrimitive.Root>
  )
})
Tooltip.displayName = "Tooltip"

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ asChild, children, ...props }, ref) => {
  if (asChild) {
    return (
      <TooltipPrimitive.Trigger ref={ref} asChild {...props}>
        {children}
      </TooltipPrimitive.Trigger>
    )
  }
  return (
    <TooltipPrimitive.Trigger ref={ref} asChild {...props}>
      <button type="button" className="inline-flex items-center">
        {children}
      </button>
    </TooltipPrimitive.Trigger>
  )
})
TooltipTrigger.displayName = "TooltipTrigger"

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-tooltip-content-transform-origin]",
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
